import React, { useMemo, useState } from 'react';
import { Box, Typography, LinearProgress, ToggleButton, ToggleButtonGroup, Tooltip } from '@mui/material';
import styled from '@emotion/styled';
import BaseWidget from './BaseWidget';
import { useFetchQueue } from '../hooks/useFetchQueue';
import { requestGetResourceUtilization } from '../redux/sagas/requests/reports';
import { toLocalDay } from '../utils/dateUtils';

const BRACKETS = [
    { key: 'b1', label: '<20', min: 0, max: 20 },
    { key: 'b2', label: '20–50', min: 20, max: 50 },
    { key: 'b3', label: '50–75', min: 50, max: 75 },
    { key: 'b4', label: '75–100', min: 75, max: 121 },
];

const bracketOf = (u) => BRACKETS.find(b => u >= b.min && u < b.max)?.key;

const FilterToggle = styled(ToggleButtonGroup)(({ theme }) => ({
    '& .MuiToggleButton-root': {
        padding: theme.spacing(0.25, 1),
        fontSize: theme.typography.size.xs,
        lineHeight: 1.2,
        textTransform: 'none',
    },
}));

const UserRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    marginBottom: theme.spacing(2),
    '&:last-child': {
        marginBottom: 0,
    },
}));

const UserInfo = styled(Box)({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 4,
});

const UserName = styled(Typography)(({ theme }) => ({
    fontWeight: theme.typography.fontWeightMedium,
    fontSize: theme.typography.size.md,
}));

const UtilizationValue = styled(Typography)(({ theme, utilization }) => ({
    fontWeight: theme.typography.fontWeightMedium,
    fontSize: theme.typography.size.md,
    color: utilization >= 80
        ? theme.palette.success.main
        : utilization >= 50
            ? theme.palette.warning.main
            : theme.palette.error.main,
}));

const StyledLinearProgress = styled(LinearProgress)(({ theme, utilization }) => ({
    height: 8,
    borderRadius: 4,
    backgroundColor: theme.palette.surface.track,
    '& .MuiLinearProgress-bar': {
        borderRadius: 4,
        backgroundColor: utilization >= 80
            ? theme.palette.success.main
            : utilization >= 50
                ? theme.palette.warning.main
                : theme.palette.error.main,
    },
}));

const EmptyState = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '150px',
    color: theme.palette.text.secondary,
}));

const ResourceUtilizationWidget = ({ widgetId, config = {}, onRemove }) => {
    // Calculate current month's date range
    const { startDate, endDate, weekdaysSoFar } = useMemo(() => {
        const now = new Date();
        const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        // Calculate number of weekdays in the month so far (from 1st to today, exclusive)
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        let weekdaysSoFar = 0;
        for (let d = new Date(startOfMonth); d < today; d.setDate(d.getDate() + 1)) {
            const day = d.getDay();
            if (day !== 0 && day !== 6) {
                weekdaysSoFar++;
            }
        }

        return {
            startDate: toLocalDay(startOfMonth),
            endDate: toLocalDay(endOfMonth),
            weekdaysSoFar,
        };
    }, []);

    const { data: utilizationData, isLoading, error } = useFetchQueue(widgetId, {
        apiCall: () => requestGetResourceUtilization(startDate, endDate, false),
        cacheKey: `resource-utilization-${startDate}-${endDate}`,
        transform: (response) => response || [],
    });

    const [activeBrackets, setActiveBrackets] = useState(BRACKETS.map(b => b.key));

    const rows = useMemo(() => {
        return (utilizationData || []).map((user) => {
            const capacitySoFar = (user.capacity || 0) * (weekdaysSoFar / 20);
            const utilization = Math.min(100, capacitySoFar > 0 ? Math.round((user.timeTracked / capacitySoFar) * 100) : 0);
            return { user, utilization, bracket: bracketOf(utilization) };
        });
    }, [utilizationData, weekdaysSoFar]);

    const filteredRows = rows.filter(r => activeBrackets.includes(r.bracket));
    const hasData = filteredRows.length > 0;

    const handleBracketChange = (_e, next) => {
        // Don't allow deselecting everything
        if (next && next.length > 0) setActiveBrackets(next);
    };

    const filterActions = (
        <Tooltip title="Filter by utilization bracket" placement="top">
            <FilterToggle
                size="small"
                value={activeBrackets}
                onChange={handleBracketChange}
                aria-label="utilization bracket filter"
                className="widget-no-drag"
            >
                {BRACKETS.map(b => (
                    <ToggleButton key={b.key} value={b.key} aria-label={`${b.label}%`}>
                        {b.label}
                    </ToggleButton>
                ))}
            </FilterToggle>
        </Tooltip>
    );

    return (
        <BaseWidget
            title="Resource Utilization"
            widgetId={widgetId}
            onRemove={onRemove}
            isLoading={isLoading}
            error={error}
            actions={filterActions}
        >
            {!hasData ? (
                <EmptyState>
                    <Typography variant="body2">
                        {rows.length === 0 ? 'No utilization data available' : 'No users in selected bracket(s)'}
                    </Typography>
                </EmptyState>
            ) : (
                <Box>
                    {filteredRows.map(({ user, utilization }, index) => (
                        <UserRow key={user._id || index}>
                            <UserInfo>
                                <UserName>{user?.employeeName || ''}</UserName>
                                <UtilizationValue utilization={utilization}>
                                    {utilization}%
                                </UtilizationValue>
                            </UserInfo>
                            <StyledLinearProgress
                                variant="determinate"
                                value={utilization}
                                utilization={utilization}
                            />
                        </UserRow>
                    ))}
                </Box>
            )}
        </BaseWidget>
    );
};

export default ResourceUtilizationWidget;
